import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation, Routes, Route, Navigate } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Loader2, Car } from 'lucide-react';
import { 
  LayoutDashboard, DollarSign, Calendar, Settings, LogOut, Menu, X, ChevronRight
} from 'lucide-react';

// Import das páginas do prestador
import ProviderDashboard from '@/pages/provider/Dashboard';
import ProviderFinancial from '@/pages/provider/Financial';
import ProviderServices from '@/pages/provider/Services';
import ProviderSettings from '@/pages/provider/Settings';

const menuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/provider/dashboard' },
  { icon: DollarSign, label: 'Financeiro', path: '/provider/financial' },
  { icon: Calendar, label: 'Serviços', path: '/provider/services' },
  { icon: Settings, label: 'Configurações', path: '/provider/settings' },
];

export default function ProviderLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/provider/login');
      return;
    }
    if (user && user.user_type !== 'provider') {
      navigate('/provider/login');
      return;
    }
    setAuthChecked(true);
  }, [user, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    window.location.reload();
  };

  const getInitials = (name) => {
    if (!name) return 'PR';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  if (!authChecked || !user) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar Desktop - Azul profundo (#0B1F3A) */}
      <aside className="hidden md:flex md:flex-col w-64 bg-[#0B1F3A] text-white fixed h-full z-20">
        {/* Logo/Header */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="bg-[#2563EB] p-2 rounded-lg">
              <Car className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg">JATOO</h1>
              <p className="text-xs text-white/60">Área do Prestador</p>
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 p-4 space-y-1">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isActive 
                    ? 'bg-[#2563EB] text-white shadow-lg shadow-blue-500/25' 
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span>{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </button>
            );
          })}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#2563EB] flex items-center justify-center text-white font-semibold">
              {getInitials(user?.business_name || user?.name)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.business_name || user?.name}</p>
              <p className="text-xs text-white/50 truncate">{user?.email}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-[#0B1F3A] text-white z-50">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <div className="bg-[#2563EB] p-1.5 rounded-lg">
              <Car className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-lg font-bold">JATOO</h1>
          </div>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => setMenuOpen(!menuOpen)}
            className="text-white hover:bg-white/10"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <nav className="p-4 space-y-2 bg-[#0B1F3A] border-t border-white/10">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => { navigate(item.path); setMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    isActive 
                      ? 'bg-[#2563EB] text-white' 
                      : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </button>
              );
            })}
            <Button 
              variant="ghost" 
              className="w-full mt-4 justify-start text-white/70 hover:text-white hover:bg-white/10" 
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </nav>
        )}
      </div>

      {/* Main Content - Rotas Aninhadas */}
      <main className="flex-1 md:ml-64 md:pt-0 pt-16">
        <Routes>
          <Route path="dashboard" element={<ProviderDashboard />} />
          <Route path="financial" element={<ProviderFinancial />} />
          <Route path="services" element={<ProviderServices />} />
          <Route path="settings" element={<ProviderSettings />} />
          <Route path="*" element={<Navigate to="/provider/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
}
